import { NgTemplateOutlet } from '@angular/common';
import { CdkDragDrop, CdkDragEnd, CdkDragMove, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { FormBuilderEditCultureService } from './form-builder-edit-culture.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import {
  FORM_FIELD_TYPES,
  FormField,
  FormFieldType,
  FormLayoutType,
  FormSchemaModel,
  FormSection,
  createField,
  readFieldPosition,
  readFieldSpan,
  readLocalizedText
} from '../../../shared/dynamic-form/form-schema.models';

@Component({
  standalone: true,
  selector: 'ain-form-builder-canvas',
  imports: [
    NgTemplateOutlet,
    DragDropModule,
    FormsModule,
    CheckboxModule,
    SelectModule,
    TabsModule,
    ButtonModule,
    AinFormFieldComponent,
    AinStateComponent
  ],
  template: `
    <section class="canvas-shell card">
      @if (layoutType() === 'wizard') {
        <div class="wizard-options">
          <div class="checkbox-row">
            <p-checkbox
              binary
              [ngModel]="wizardOneQuestion()"
              [disabled]="!canEdit()"
              (ngModelChange)="updateWizardOption('oneQuestionPerStep', $event)" />
            <label>{{ t('dynamic.builder.canvas.wizard.oneQuestionPerStep') }}</label>
          </div>
          <div class="checkbox-row">
            <p-checkbox
              binary
              [ngModel]="wizardShowProgress()"
              [disabled]="!canEdit()"
              (ngModelChange)="updateWizardOption('showProgress', $event)" />
            <label>{{ t('dynamic.builder.canvas.wizard.showProgress') }}</label>
          </div>
          <div class="checkbox-row">
            <p-checkbox
              binary
              [ngModel]="wizardAllowBack()"
              [disabled]="!canEdit()"
              (ngModelChange)="updateWizardOption('allowBack', $event)" />
            <label>{{ t('dynamic.builder.canvas.wizard.allowBack') }}</label>
          </div>
          <ain-form-field [label]="t('dynamic.builder.canvas.wizard.stepValidation')">
            <p-select
              [options]="stepValidationOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="wizardStepValidation()"
              [disabled]="!canEdit()"
              appendTo="body"
              (ngModelChange)="updateWizardOption('stepValidation', $event)" />
          </ain-form-field>
        </div>
      }

      @switch (layoutType()) {
        @case ('tabbed') {
          <p-tabs [value]="activeSectionKey() ?? ''" (valueChange)="onTabChange($event)">
            <p-tablist>
              @for (section of schema().sections; track section.key) {
                <p-tab [value]="section.key">{{ sectionTitle(section) }}</p-tab>
              }
            </p-tablist>
            <p-tabpanels>
              @for (section of schema().sections; track section.key) {
                <p-tabpanel [value]="section.key">
                  <ng-container *ngTemplateOutlet="fieldList; context: { $implicit: section }" />
                </p-tabpanel>
              }
            </p-tabpanels>
          </p-tabs>
        }
        @case ('accordion') {
          @for (section of schema().sections; track section.key) {
            <details class="accordion-panel" [open]="section.key === activeSectionKey()">
              <summary (click)="selectSection(section.key)">{{ sectionTitle(section) }}</summary>
              <ng-container *ngTemplateOutlet="fieldList; context: { $implicit: section }" />
            </details>
          }
        }
        @case ('wizard') {
          @if (activeSection(); as section) {
            <div class="wizard-step-header">
              <strong>{{ t('dynamic.builder.canvas.wizard.currentStep') }}: {{ sectionTitle(section) }}</strong>
            </div>
            <ng-container *ngTemplateOutlet="fieldList; context: { $implicit: section }" />
          }
        }
        @case ('custom') {
          @if (activeSection(); as customSection) {
            <div
              class="custom-canvas"
              cdkDropList
              [cdkDropListData]="customSection.fields"
              (cdkDropListDropped)="onFieldDrop($event, customSection)">
              @if (!customSection.fields.length) {
                <ain-state type="empty" [title]="t('dynamic.builder.canvas.empty')" />
              }
              @for (field of customSection.fields; track field.key) {
                <article
                  class="canvas-field custom-node"
                  cdkDrag
                  [cdkDragDisabled]="!canEdit()"
                  [class.selected]="field.key === selectedFieldKey()"
                  [style.left.px]="positionFor(field).x"
                  [style.top.px]="positionFor(field).y"
                  (click)="selectField(field.key)"
                  (cdkDragMoved)="onCustomDragMoved(field, $event)"
                  (cdkDragEnded)="onCustomDragEnded(field, $event)">
                  <ng-container *ngTemplateOutlet="fieldCard; context: { $implicit: field }" />
                </article>
              }
            </div>
          }
        }
        @default {
          @if (activeSection(); as section) {
            <ng-container *ngTemplateOutlet="fieldList; context: { $implicit: section }" />
          } @else {
            <ain-state type="empty" [title]="t('dynamic.builder.canvas.noSection')" />
          }
        }
      }
    </section>

    <ng-template #fieldList let-section>
      <div
        class="field-drop-list"
        [class.grid-layout]="isGridLayout()"
        [style.--canvas-columns]="columnCount(section)"
        cdkDropList
        [id]="dropListId(section.key)"
        [cdkDropListData]="section.fields"
        [cdkDropListConnectedTo]="connectedDropLists()"
        (cdkDropListDropped)="onFieldDrop($event, section)">
        @if (!section.fields.length) {
          <div class="drop-placeholder">{{ t('dynamic.builder.canvas.dropHint') }}</div>
        }
        @for (field of section.fields; track field.key) {
          <article
            class="canvas-field"
            cdkDrag
            [cdkDragDisabled]="!canEdit()"
            [class.selected]="field.key === selectedFieldKey()"
            [style.gridColumn]="gridColumn(field)"
            (click)="selectField(field.key)">
            <ng-container *ngTemplateOutlet="fieldCard; context: { $implicit: field }" />
          </article>
        }
      </div>
    </ng-template>

    <ng-template #fieldCard let-field>
      <div class="field-card">
        @if (canEdit()) {
          <span class="drag-handle" cdkDragHandle [attr.aria-label]="t('dynamic.builder.canvas.dragHandle')"><i class="pi pi-bars" aria-hidden="true"></i></span>
        }
        <div class="field-body">
          <strong>{{ fieldTitle(field) }}</strong>
          <span>{{ fieldTypeLabel(field.type) }}</span>
          <div class="field-badges">
            @if (field.required) {
              <small class="badge">{{ t('dynamic.form.requiredField') }}</small>
            }
            @if (field.type === 'computed' || field.computed) {
              <small class="badge badge-computed">{{ t('dynamic.builder.canvas.badgeComputed') }}</small>
            }
            @if (field.hidden || field.type === 'hidden') {
              <small class="badge badge-hidden">{{ t('dynamic.builder.canvas.badgeHidden') }}</small>
            }
          </div>
        </div>
        @if (canEdit() && field.key === selectedFieldKey()) {
          <button
            pButton
            type="button"
            text
            severity="danger"
            icon="pi pi-trash"
            [attr.aria-label]="t('dynamic.builder.properties.removeField')"
            (click)="removeField.emit(); $event.stopPropagation()"></button>
        }
      </div>
    </ng-template>
  `,
  styles: [`
    .canvas-shell { display: grid; gap: 12px; min-height: 360px; }
    .wizard-options {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e2e8f0;
    }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .wizard-step-header { margin-bottom: 8px; color: #0f172a; }
    .field-drop-list {
      display: grid;
      gap: 8px;
      min-height: 220px;
      padding: 8px;
      border: 1px dashed #cbd5e1;
      border-radius: 10px;
      background: #f8fafc;
    }
    .field-drop-list.grid-layout {
      grid-template-columns: repeat(var(--canvas-columns, 1), minmax(0, 1fr));
    }
    .drop-placeholder {
      grid-column: 1 / -1;
      padding: 24px;
      text-align: center;
      color: #64748b;
      border: 1px dashed #cbd5e1;
      border-radius: 8px;
      background: #fff;
    }
    .canvas-field { cursor: pointer; }
    .canvas-field.selected .field-card { border-color: #0f766e; background: #ecfdf5; }
    .field-card {
      display: flex;
      gap: 8px;
      align-items: flex-start;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      background: #fff;
      padding: 10px;
    }
    .drag-handle { cursor: grab; color: #64748b; }
    .field-body { display: grid; gap: 2px; flex: 1; }
    .field-body span, .field-body small { color: #64748b; font-size: 0.8rem; }
    .field-badges { display: flex; flex-wrap: wrap; gap: 4px; }
    .badge {
      display: inline-flex;
      padding: 1px 6px;
      border-radius: 999px;
      background: #f1f5f9;
      color: #475569;
    }
    .badge-computed { background: #ecfeff; color: #0e7490; }
    .badge-hidden { background: #fef3c7; color: #92400e; }
    .accordion-panel {
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 8px 10px;
      background: #fff;
    }
    .accordion-panel summary {
      cursor: pointer;
      font-weight: 600;
      margin-bottom: 8px;
    }
    .custom-canvas {
      position: relative;
      min-height: 520px;
      border: 1px dashed #cbd5e1;
      border-radius: 10px;
      background: #f8fafc;
      overflow: auto;
    }
    .custom-node {
      position: absolute;
      width: 240px;
      z-index: 1;
    }
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 12px;
      background: #fff;
    }
  `]
})
export class FormBuilderCanvasComponent {
  private readonly localization = inject(LocalizationService);
  private readonly editCultureService = inject(FormBuilderEditCultureService);

  readonly schema = input.required<FormSchemaModel>();
  readonly culture = input.required<string>();
  readonly layoutType = input.required<FormLayoutType>();
  readonly activeSectionKey = input<string | null>(null);
  readonly selectedFieldKey = input<string | null>(null);
  readonly canEdit = input(true);

  readonly schemaChange = output<FormSchemaModel>();
  readonly activeSectionChange = output<string>();
  readonly selectedFieldChange = output<string | null>();
  readonly removeField = output<void>();
  readonly fieldAdded = output<{ sectionKey: string; field: FormField }>();

  readonly activeSection = computed(() =>
    this.schema().sections.find(section => section.key === this.activeSectionKey())
    ?? this.schema().sections[0]
    ?? null
  );

  readonly connectedDropLists = computed(() =>
    this.schema().sections.map(section => this.dropListId(section.key))
  );

  readonly wizardOneQuestion = computed(() => !!this.schema().layout.wizard?.oneQuestionPerStep);
  readonly wizardShowProgress = computed(() => this.schema().layout.wizard?.showProgress !== false);
  readonly wizardAllowBack = computed(() => this.schema().layout.wizard?.allowBack !== false);
  readonly wizardStepValidation = computed(() => this.schema().layout.wizard?.stepValidation ?? 'onNext');

  private readonly customPositions = signal<Record<string, { x: number; y: number }>>({});

  dropListId(sectionKey: string): string {
    return `builder-section-${sectionKey}`;
  }

  isGridLayout(): boolean {
    return ['twoColumn', 'threeColumn', 'grid'].includes(this.layoutType());
  }

  columnCount(section: FormSection): number {
    if (this.layoutType() === 'twoColumn') {
      return section.columns ?? 2;
    }
    if (this.layoutType() === 'threeColumn') {
      return section.columns ?? 3;
    }
    if (this.layoutType() === 'grid') {
      return section.columns ?? this.schema().layout.columns ?? 12;
    }
    return 1;
  }

  gridColumn(field: FormField): string {
    const span = readFieldSpan(field);
    return span > 1 ? `span ${Math.min(span, this.columnCount(this.activeSection()!))}` : 'auto';
  }

  sectionTitle(section: FormSection): string {
    return readLocalizedText(section.title, this.editCultureService.editCulture()) || section.key;
  }

  fieldTitle(field: FormField): string {
    return readLocalizedText(field.label, this.editCultureService.editCulture()) || field.key;
  }

  fieldTypeLabel(type: string): string {
    const catalog = FORM_FIELD_TYPES.find(item => item.value === type);
    return catalog ? this.t(catalog.labelKey) : type;
  }

  stepValidationOptions(): { label: string; value: string }[] {
    return [
      { label: this.t('dynamic.builder.canvas.wizard.validationOnNext'), value: 'onNext' },
      { label: this.t('dynamic.builder.canvas.wizard.validationOnSubmit'), value: 'onSubmit' }
    ];
  }

  onTabChange(value: string | number | undefined): void {
    if (typeof value === 'string') {
      this.activeSectionChange.emit(value);
    }
  }

  selectSection(sectionKey: string): void {
    this.activeSectionChange.emit(sectionKey);
  }

  selectField(fieldKey: string): void {
    this.selectedFieldChange.emit(fieldKey);
  }

  positionFor(field: FormField): { x: number; y: number } {
    const position = readFieldPosition(field);
    return { x: position?.x ?? 24, y: position?.y ?? 24 };
  }

  updateWizardOption(key: 'oneQuestionPerStep' | 'showProgress' | 'allowBack' | 'stepValidation', value: boolean | string): void {
    const layout = {
      ...this.schema().layout,
      wizard: {
        ...this.schema().layout.wizard,
        [key]: value
      }
    };
    this.schemaChange.emit({ ...this.schema(), layout });
  }

  addFieldToActiveSection(type: FormFieldType): void {
    const section = this.activeSection();
    if (!section) {
      return;
    }
    const field = createField(type, this.culture(), this.schema().sections.flatMap(item => item.fields.map(f => f.key)));
    if (this.layoutType() === 'custom') {
      const index = section.fields.length;
      const positioned: FormField = {
        ...field,
        position: { x: 24 + (index % 3) * 260, y: 24 + Math.floor(index / 3) * 120 }
      };
      this.insertField(section.key, positioned);
      return;
    }
    this.insertField(section.key, field);
  }

  insertField(sectionKey: string, field: FormField): void {
    const sections = this.schema().sections.map(section =>
      section.key === sectionKey
        ? { ...section, fields: [...section.fields, field] }
        : section
    );
    this.schemaChange.emit({ ...this.schema(), sections });
    this.fieldAdded.emit({ sectionKey, field });
    this.selectedFieldChange.emit(field.key);
  }

  onFieldDrop(event: CdkDragDrop<FormField[]>, section: FormSection): void {
    if (!this.canEdit()) {
      return;
    }

    const sections = this.schema().sections.map(item => ({
      ...item,
      fields: [...item.fields]
    }));

    const target = sections.find(item => item.key === section.key);
    if (!target) {
      return;
    }

    if (event.previousContainer !== event.container && typeof event.item.data === 'string') {
      const field = createField(
        event.item.data as FormFieldType,
        this.culture(),
        this.schema().sections.flatMap(item => item.fields.map(entry => entry.key))
      );
      if (this.layoutType() === 'custom') {
        const positioned: FormField = {
          ...field,
          position: { x: 24, y: 24 + target.fields.length * 120 }
        };
        target.fields.splice(event.currentIndex, 0, positioned);
      } else {
        target.fields.splice(event.currentIndex, 0, field);
      }
      this.schemaChange.emit({ ...this.schema(), sections });
      this.fieldAdded.emit({ sectionKey: section.key, field: target.fields[event.currentIndex] });
      this.selectedFieldChange.emit(target.fields[event.currentIndex].key);
      return;
    }

    if (event.previousContainer === event.container) {
      moveItemInArray(target.fields, event.previousIndex, event.currentIndex);
    } else {
      const sourceSection = sections.find(item => this.dropListId(item.key) === event.previousContainer.id);
      if (!sourceSection) {
        return;
      }
      transferArrayItem(sourceSection.fields, target.fields, event.previousIndex, event.currentIndex);
    }

    this.schemaChange.emit({ ...this.schema(), sections });
  }

  onCustomDragMoved(field: FormField, event: CdkDragMove): void {
    const position = event.source.getFreeDragPosition();
    this.customPositions.update(current => ({
      ...current,
      [field.key]: { x: position.x, y: position.y }
    }));
  }

  onCustomDragEnded(field: FormField, event: CdkDragEnd): void {
    const position = event.source.getFreeDragPosition();
    const sections = this.schema().sections.map(section => ({
      ...section,
      fields: section.fields.map(item =>
        item.key === field.key
          ? { ...item, position: { x: position.x, y: position.y } }
          : item
      )
    }));
    this.schemaChange.emit({ ...this.schema(), sections });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
